import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Lock, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import VenusLogo from "../components/VenusLogo";

/**
 * Mandatory password change screen.
 *
 * Shown automatically when the logged-in user has `must_change_password = true`
 * (set by the backend at invite time). The user CANNOT navigate anywhere else
 * until they pick a strong password.
 *
 * Once submitted, the backend clears the flag and we refresh the local user
 * context.
 */
export default function ForcePasswordChange() {
  const { user, setUser, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const noDigit = newPassword.length >= 8 && !/\d/.test(newPassword);
  const noLetter = newPassword.length >= 8 && !/[A-Za-z]/.test(newPassword);
  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const formValid = currentPassword.length > 0 && newPassword.length >= 8
    && /\d/.test(newPassword) && /[A-Za-z]/.test(newPassword) && newPassword === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!formValid) {
      return toast.error("Le mot de passe ne respecte pas les règles");
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Mot de passe mis à jour");
      // Refresh the user profile so the flag clears
      const { data } = await api.get("/auth/me");
      setUser({ ...user, ...data, must_change_password: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF4F8] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_40px_-12px_rgba(194,86,155,0.25)] p-8">
        <div className="flex flex-col items-center gap-3 mb-6">
          <VenusLogo size={64} />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--primary-soft,#F8E8F1)] text-[var(--primary-dark,#8E3A6D)] text-xs font-bold uppercase tracking-wider">
            <ShieldCheck size={14}/> Sécurité requise
          </div>
        </div>

        <h1 className="font-heading text-2xl font-extrabold text-[var(--text)] text-center">
          Définissez votre mot de passe
        </h1>
        <p className="text-sm text-[var(--text-muted)] text-center mt-2">
          Pour votre sécurité, vous devez remplacer votre mot de passe temporaire
          avant de continuer.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="force-pw-form">
          <div>
            <label className="label-mono block mb-1.5">Mot de passe actuel (temporaire)</label>
            <input
              data-testid="current-password"
              type="password"
              required
              autoFocus
              className="input-base"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Celui reçu par SMS"
            />
          </div>

          <div>
            <label className="label-mono block mb-1.5">Nouveau mot de passe</label>
            <input
              data-testid="new-password"
              type="password"
              required
              className="input-base"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Au moins 8 caractères"
            />
            <ul className="mt-2 text-xs space-y-1">
              <li className={`flex items-center gap-1.5 ${newPassword.length >= 8 ? "text-emerald-600" : tooShort ? "text-rose-600" : "text-[var(--text-muted)]"}`}>
                <Lock size={11}/> Au moins 8 caractères
              </li>
              <li className={`flex items-center gap-1.5 ${/[A-Za-z]/.test(newPassword) ? "text-emerald-600" : noLetter ? "text-rose-600" : "text-[var(--text-muted)]"}`}>
                <Lock size={11}/> Au moins une lettre
              </li>
              <li className={`flex items-center gap-1.5 ${/\d/.test(newPassword) ? "text-emerald-600" : noDigit ? "text-rose-600" : "text-[var(--text-muted)]"}`}>
                <Lock size={11}/> Au moins un chiffre
              </li>
            </ul>
          </div>

          <div>
            <label className="label-mono block mb-1.5">Confirmer le nouveau mot de passe</label>
            <input
              data-testid="confirm-password"
              type="password"
              required
              className="input-base"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {mismatch && <div className="text-xs text-rose-600 mt-1">Les mots de passe ne correspondent pas</div>}
          </div>

          <button
            type="submit"
            data-testid="submit-new-password"
            disabled={submitting || !formValid}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? "Sauvegarde..." : <>VALIDER ET CONTINUER <ArrowRight size={16}/></>}
          </button>

          <button
            type="button"
            data-testid="logout-from-force"
            onClick={logout}
            className="w-full text-xs text-[var(--text-muted)] hover:text-[var(--text)] underline text-center"
          >
            Me déconnecter
          </button>
        </form>
      </div>
    </div>
  );
}
